import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';
import { Microfrontend } from '@cian/mf-react';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { requestAuthentication } from 'shared/actions/authentication';
import { Dispatch } from 'shared/common/state';
import { NewbuildingBrokerFallbackModal } from 'shared/components/NewbuildingBrokerFallbackModal';
import { selectSource } from 'shared/selectors';
import { selectNewbuildingBrokerConsultantBannerButtonText } from 'shared/selectors/newbuildingBrokerConsultantBanner';
import { selectFirstGaLabel } from 'shared/selectors/selectFirstGaLabel';
import { getUser } from 'shared/selectors/user';
import { IOpenDIYModalParams } from 'shared/types/microfrontends';

import { NewbuildingBrokerConsultantBanner } from './NewbuildingBrokerConsultantBanner';
import { useFallbackModalSettings, useMicrofrontAPI, useMicrofrontEvents, useRegion } from './hooks';
import { trackBannerClick, trackBannerShow } from './tracking';
import { TOpenMicrofrontResultHandler } from './types';

export const NewbuildingBrokerConsultantBannerContainer: React.FC = () => {
  const buttonText = useSelector(selectNewbuildingBrokerConsultantBannerButtonText);
  const user = useSelector(getUser);
  const region = useRegion();
  const gaLabel = useSelector(selectFirstGaLabel);
  const source = useSelector(selectSource);

  const [isLoading, setIsLoading] = React.useState(false);
  const fallbackModalSettings = useFallbackModalSettings();

  const [isFallbackModalVisible, setFallbackModalVisibility] = React.useState(false);
  const closeFallbackModal = React.useCallback(() => setFallbackModalVisibility(false), []);
  const showFallbackModal = React.useCallback(() => setFallbackModalVisibility(true), []);

  const dispatch: Dispatch = useDispatch();

  const finishMicrofrontOpening = React.useCallback<TOpenMicrofrontResultHandler>(
    ({ isError }) => {
      if (isError) {
        showFallbackModal();
        setIsLoading(false);
      } else {
        if (isFallbackModalVisible) {
          closeFallbackModal();
        }

        // снятие loading произойдет по событию из микрофронта.
      }
    },
    [isFallbackModalVisible, showFallbackModal, closeFallbackModal],
  );

  const { openMicrofront, microfrontManifest, microfrontRef } = useMicrofrontAPI(finishMicrofrontOpening);

  useMicrofrontEvents(microfrontManifest, setIsLoading);

  // Реф нужен, чтобы получать свежего user в коллбэке во избежание проблемы с замыканием на старое значение.
  const userRef = React.useRef(user);

  React.useEffect(() => {
    userRef.current = user;
  }, [user]);

  const startMicrofrontOpening = React.useCallback(() => {
    setIsLoading(true);
    openMicrofront({
      user: userRef.current,
      region,
      gaLabel,
      source,
    } as IOpenDIYModalParams);
  }, [region, gaLabel, source, openMicrofront]);

  const handleButtonClick = React.useCallback(() => {
    trackBannerClick();

    if (!userRef.current.isAuthenticated) {
      dispatch(
        requestAuthentication({
          onSuccess: () => () => startMicrofrontOpening(),
        }),
      );

      return;
    }

    startMicrofrontOpening();
  }, [dispatch, startMicrofrontOpening]);

  return (
    <>
      <ActionAfterViewObserver callback={trackBannerShow}>
        <NewbuildingBrokerConsultantBanner
          buttonText={buttonText}
          buttonLoading={isLoading}
          onClick={handleButtonClick}
        />
      </ActionAfterViewObserver>
      {isFallbackModalVisible && (
        <NewbuildingBrokerFallbackModal
          {...fallbackModalSettings}
          loading={isLoading}
          onClose={closeFallbackModal}
          onRetry={startMicrofrontOpening}
        />
      )}
      {microfrontManifest && (
        <Microfrontend
          ref={microfrontRef}
          name={microfrontManifest.microfrontendName}
          image={microfrontManifest.imageVersion}
          runtime={microfrontManifest.runtimeName}
          instance={microfrontManifest.instanceId}
        />
      )}
    </>
  );
};
